/**
 * Per-client hardware features (`docs/CLIENT_FEATURES_SERVER.md`). Set by the operator in the
 * back office on the client record; the app only reads them to hide what that client's hardware
 * cannot report — fuel without a CAN reader, mileage without an odometer. Nothing is deleted.
 *
 * Shared by the browser and the scheduled-report renderer, so it stays free of React/DOM.
 */

export const CLIENT_FEATURES = ["fuel", "odometer"] as const;

export type ClientFeature = (typeof CLIENT_FEATURES)[number];

export type ClientFeatures = Readonly<Record<ClientFeature, boolean>>;

export const ALL_FEATURES_ON: ClientFeatures = Object.freeze({ fuel: true, odometer: true });

function isExplicitOff(value: unknown): boolean {
  if (value === false || value === 0) return true;
  if (typeof value === "string") {
    const text = value.trim().toLowerCase();
    return text === "0" || text === "false";
  }
  return false;
}

/**
 * Lenient, like `track_speeding`: `1`/`0`, `"1"`/`"0"`, booleans, or a JSON-encoded object.
 * A missing field or key means **on** — today's behaviour for every client — so only an explicit
 * off hides anything.
 */
export function parseClientFeatures(raw: unknown): ClientFeatures {
  let source = raw;
  if (typeof source === "string" && source.trim().startsWith("{")) {
    try {
      source = JSON.parse(source);
    } catch {
      return ALL_FEATURES_ON;
    }
  }
  if (!source || typeof source !== "object" || Array.isArray(source)) return ALL_FEATURES_ON;

  const record = source as Record<string, unknown>;
  let anyOff = false;
  const result = {} as Record<ClientFeature, boolean>;
  for (const feature of CLIENT_FEATURES) {
    const on = !isExplicitOff(record[feature]);
    result[feature] = on;
    if (!on) anyOff = true;
  }
  return anyOff ? result : ALL_FEATURES_ON;
}
